import { AxiosResponse } from 'axios';
import { action, runInAction } from 'mobx';

import { notUndefined } from 'ui/base/types';

import { ServiceStatus, ResultKind } from 'services/base/types';
import { getResponseBytes, http, isHttpError } from 'services/base/http';
import { CountryCode } from 'services/base/countries';
import { Pangolin, PlanFirewallPackage } from 'services/accounts/types';
import { authorizePlanFirewall } from 'services/accounts/logic/plans/plan_firewall';

import { fromBoolean } from 'assets/enums/actions/actions';
import { fromHistoryDirection } from 'assets/enums/directions/directions';
import { TransportProtocol, fromTransportProtocolNumber } from 'assets/enums/protocols/protocols';

import {
  GetNetworkHistoryResponse,
  NetworkHistoryDirection,
  NetworkHistoryObject,
  NetworkHistoryLocationObject,
} from './protos/network_pb';

import {
  IPangolinDomain,
  History,
  HistoryMap,
  HistoryIndex,
  HistoryLocation,
  HistoryFilter,

  ProtocolFilterType,
  ApplicationProtocolFilter,
  PortTransportProtocolFilter,
  RangeTransportProtocolFilter,
  APPLICATION_PROTOCOL_FILTERS,

  SiteFilterType,
  DomainSiteFilter,
  AddressSiteFilter,
  RangeSiteFilter,
  NetworkSiteFilter,
} from './types';

export const LIMIT_PER_PAGE_DEFAULT = 100;
export const LIMIT_PER_PAGE_NOPLAN = 5;
export const LIMIT_LOADED = 100000;

export type HistoryFilterApplyResult =
  | { kind: ResultKind.OK; filter: HistoryFilter }
  | {
    kind: ResultKind.SYSTEM_ERROR;
    status?: number;
  }
  | {
    kind: ResultKind.USER_ERROR;
    status?: number;
    directionsError?: string;
    actionsError?: string;
  };

export type HistoryFetchItemResult =
  | { kind: ResultKind.OK; item: History | undefined }
  | { kind: ResultKind.SYSTEM_ERROR; status?: number }
  | { kind: ResultKind.USER_ERROR; status?: number };

export type HistoryFetchPageResult =
  | { kind: ResultKind.OK; history: HistoryMap }
  | { kind: ResultKind.SYSTEM_ERROR; status?: number }
  | { kind: ResultKind.USER_ERROR; status?: number };

export class HistoryService {
  @action
  static clearData(domain: IPangolinDomain) {
    domain.history = new HistoryMap();
    domain.historyOffset = 0;
    domain.historyCapped = false;
    domain.historyStatus = ServiceStatus.FRESH;
  }

  @action
  static clearFilter(domain: IPangolinDomain) {
    const now = new Date();
    domain.historyFilter = {
      start: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1,
                      now.getHours(), now.getMinutes(), now.getSeconds()),
      end: new Date(now.getFullYear(), now.getMonth(), now.getDate(),
                    now.getHours(), now.getMinutes(), now.getSeconds()),
      directions: { outbound: true, inbound: true, local: true },
      actions: { allowed: true, blocked: true },
      protocols: [], sites: [],
      devices: [], locations: [],
    };
  }

  @action
  static clearFilterDate(domain: IPangolinDomain) {
    const now = new Date();
    domain.historyFilter = {
      ...domain.historyFilter,
      start: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1,
                      now.getHours(), now.getMinutes(), now.getSeconds()),
      end: new Date(now.getFullYear(), now.getMonth(), now.getDate(),
                    now.getHours(), now.getMinutes(), now.getSeconds()),
    };
  }

  @action
  static clearAll(domain: IPangolinDomain) {
    HistoryService.clearData(domain);
    HistoryService.clearFilter(domain);
  }

  @action
  static clearDate(domain: IPangolinDomain) {
    HistoryService.clearData(domain);
    HistoryService.clearFilterDate(domain);
  }

  @action
  static async fetchItem(domain: IPangolinDomain, pangolin: Pangolin, id: string, save = true): Promise<HistoryFetchItemResult> {
    try {
      // deserialize the id string into an index object

      const index = deserializeHistoryIndex(id);
      if (index == null) {
        return Promise.resolve({
          kind: ResultKind.USER_ERROR,
        });
      }

      // there is no way to directly query an item
      // therefore, we have to fetch pages to find it

      const authorized = authorizePlanFirewall(pangolin, PlanFirewallPackage.BASIC);
      const limit = authorized ? LIMIT_PER_PAGE_DEFAULT : LIMIT_PER_PAGE_NOPLAN;

      let offset = 0;

      // eslint-disable-next-line no-constant-condition
      while (true) {

        // find the desired item around the timestamp in index.start

        // eslint-disable-next-line no-await-in-loop
        const httpResp = await http.get(`/pangolin/${domain.id}/history`, {
          params: {
            until: index.start + 1,
            since: index.start - 1,
            offset, limit,
          },
        });

        // parse the page

        const page = parseHistoryResponse(httpResp);

        const first = page.getAt(0);
        const last = page.getAt(page.size - 1);

        // return error if page is empty

        if (page.size === 0) {
          return runInAction(() => {
            return Promise.resolve({
              kind: ResultKind.USER_ERROR,
            });
          });
        }

        if (first == null) {
          return runInAction(() => {
            return Promise.resolve({
              kind: ResultKind.USER_ERROR,
            });
          });
        }

        if (last == null) {
          return runInAction(() => {
            return Promise.resolve({
              kind: ResultKind.USER_ERROR,
            });
          });
        }

        // return error if page is out of bounds

        if (!(last.start <= index.start || index.start <= first.start)) {
          return runInAction(() => {
            return Promise.resolve({
              kind: ResultKind.USER_ERROR,
            });
          });
        }

        // search the page if index.start is inside of it

        if (last.start <= index.start && index.start <= first.start) {
          for (let i = page.size - 1; 0 <= i; i--) {
            const item = page.getAt(i);
            if (item != null) {
              const found = item.start === index.start &&
                            item.order === index.order &&
                            item.ctid === index.ctid;
              if (found) {
                return runInAction(() => {
                  if (save) domain.historyItem = item;
                  return Promise.resolve({
                    kind: ResultKind.OK, item,
                  });
                });
              }
            }
          }
        }

        offset += page.size;
        continue;
      }
    } catch(error) {
      return runInAction(() => {
        if (isHttpError(error)) {
          if (error.response?.status && 400 <= error.response.status && error.response.status < 500) {
            return Promise.resolve({ kind: ResultKind.USER_ERROR, status: error.response.status });
          }
          if (error.response?.status && 500 <= error.response.status && error.response.status < 600) {
            return Promise.resolve({ kind: ResultKind.SYSTEM_ERROR, status: error.response.status });
          }
        }
        throw error;
      });
    }
  }

  @action
  static async fetchPage(domain: IPangolinDomain, pangolin: Pangolin, save = true): Promise<HistoryFetchPageResult> {
    try {
      const authorized = authorizePlanFirewall(pangolin, PlanFirewallPackage.BASIC);
      const limit = authorized ? LIMIT_PER_PAGE_DEFAULT : LIMIT_PER_PAGE_NOPLAN;

      const query = domain.historyFilter;
      const until = query.end.getTime();
      const since = query.start.getTime();
      const offset = domain.historyOffset;

      if (save) domain.historyStatus = ServiceStatus.LOADING;
      const httpResp = await http.get(`/pangolin/${domain.id}/history`, {
        params: {
          until: Math.floor(until / 1000),
          since: Math.floor(since / 1000),
          offset, limit,
        },
      });

      if (domain.historyFilter.end.getTime() !== until) {
        return runInAction(() => {
          if (save) domain.historyStatus = ServiceStatus.LOADED;
          return Promise.resolve({
            kind: ResultKind.OK,
            history: domain.history,
          });
        });
      }

      if (domain.historyFilter.start.getTime() !== since) {
        return runInAction(() => {
          if (save) domain.historyStatus = ServiceStatus.LOADED;
          return Promise.resolve({
            kind: ResultKind.OK,
            history: domain.history,
          });
        });
      }

      return runInAction(() => {
        const page = parseHistoryResponse(httpResp);
        const filter = createHistoryFilterFunction(domain, query);
        const history = page.filter(filter);
        if (save) {
          domain.history.setOrderedMap(history);
          domain.historyOffset = offset + page.size;
          domain.historyCapped = page.size < limit || domain.history.size > LIMIT_LOADED;
          domain.historyStatus = ServiceStatus.LOADED;
        }
        return Promise.resolve({
          kind: ResultKind.OK, history,
        });
      });
    } catch(error) {
      return runInAction(() => {
        if (save) domain.historyStatus = ServiceStatus.FAILED;
        if (isHttpError(error)) {
          if (error.response?.status && 400 <= error.response.status && error.response.status < 500) {
            return Promise.resolve({ kind: ResultKind.USER_ERROR, status: error.response.status });
          }
          if (error.response?.status && 500 <= error.response.status && error.response.status < 600) {
            return Promise.resolve({ kind: ResultKind.SYSTEM_ERROR, status: error.response.status });
          }
        }
        throw error;
      });
    }
  }
}

function ipToNumber(ip: string): number {
  const d = ip.split('.');
  return ((((((+d[0])*256)+(+d[1]))*256)+(+d[2]))*256)+(+d[3]);
}

function netFirst(base: number, mask: number) {
  return (base & ((0xffffffff << (32 - mask)) >>> 0)) >>> 0;
}

function netFinal(base: number, mask: number) {
  return (base | (~(0xffffffff << (32 - mask)))) >>> 0;
}

function pushOneIfNotUndefined<T>(list: T[], value: T | undefined) {
  if (value !== undefined) list.push(value);
}

function pushManyIfNotUndefined<T>(list: T[], values: T[] | undefined) {
  if (values !== undefined) Array.prototype.push.apply(list, values);
}

function createHistoryFilterFunction(domain: IPangolinDomain, filter: HistoryFilter) {
  return (history: History) => {
    // Filter direction
    const direction = fromHistoryDirection(history.direction);
    if (direction == null) return false;
    if (!filter.directions[direction]) return false;

    // Filter action
    const action = fromBoolean(history.allowed);
    if (action == null) return false;
    if (!filter.actions[action]) return false;

    // Filter protocols
    if (filter.protocols.length > 0) {
      const transport = fromTransportProtocolNumber(history.proto_l4);

      // Prepare initial transport filters
      const portFilters = filter.protocols.filter((f) => f.type === ProtocolFilterType.TRANSPORT_PORT);
      const rangeFilters = filter.protocols.filter((f) => f.type === ProtocolFilterType.TRANSPORT_RANGE);

      // Digest application filters into transport filters
      filter.protocols.filter((f) => f.type === ProtocolFilterType.APPLICATION).forEach((f) => {
        const appFilter = f as ApplicationProtocolFilter;
        const appDetails = APPLICATION_PROTOCOL_FILTERS.get(appFilter.application);
        if (appDetails !== undefined) {
          const appPortFilters = appDetails.filters.transport.filter((t) => t.type === ProtocolFilterType.TRANSPORT_PORT);
          pushManyIfNotUndefined(portFilters, appPortFilters);

          const appRangeFilters = appDetails.filters.transport.filter((t) => t.type === ProtocolFilterType.TRANSPORT_RANGE);
          pushManyIfNotUndefined(rangeFilters, appRangeFilters);
        }
      });

      // Filter protocols by port
      if (portFilters.length > 0) {
        const ports: [TransportProtocol, number][] = [];
        pushOneIfNotUndefined(ports, [transport, history.port_src]);
        pushOneIfNotUndefined(ports, [transport, history.port_dst]);
        if (ports.length === 0) return false;

        const found = portFilters.find((a) => ports.find(([transport, port]) => {
          const portFilter = a as PortTransportProtocolFilter;
          return ((portFilter.transport === undefined ||
                   portFilter.transport === transport) &&
                  portFilter.port === port);
        }));
        if (!found) return false;
      }

      // Filter protocols by range
      if (rangeFilters.length > 0) {
        const ports: [TransportProtocol, number][] = [];
        pushOneIfNotUndefined(ports, [transport, history.port_src]);
        pushOneIfNotUndefined(ports, [transport, history.port_dst]);
        if (ports.length === 0) return false;

        const found = rangeFilters.find((a) => ports.find(([transport, port]) => {
          const rangeFilter = a as RangeTransportProtocolFilter;
          return ((rangeFilter.transport === undefined ||
                   rangeFilter.transport === transport) &&
                  rangeFilter.port_first <= port &&
                  port <= rangeFilter.port_final);
        }));
        if (!found) return false;
      }
    }

    // Filter sites
    if (filter.sites.length > 0) {
      // Filter sites by domain
      const domainFilters = filter.sites.filter((f) => f.type === SiteFilterType.DOMAIN);
      if (domainFilters.length > 0) {
        const domains: string[] = [];
        pushManyIfNotUndefined(domains, history.domains_src);
        pushManyIfNotUndefined(domains, history.domains_dst);
        if (domains.length === 0) return false;

        const found = domainFilters.find((a) => domains.find((b) => {
          const domainFilter = a as DomainSiteFilter;
          return b.endsWith(domainFilter.domain);
        }));
        if (!found) return false;
      }

      // Filter sites by address
      const addressFilters = filter.sites.filter((f) => f.type === SiteFilterType.ADDRESS);
      if (addressFilters.length > 0) {
        const addresses: string[] = [];
        pushOneIfNotUndefined(addresses, history.addr_src);
        pushOneIfNotUndefined(addresses, history.addr_dst);
        if (addresses.length === 0) return false;

        const found = addressFilters.find((a) => addresses.find((b) => {
          const addressFilter = a as AddressSiteFilter;
          return b === addressFilter.address;
        }));
        if (!found) return false;
      }

      // Filter sites by range
      const rangeFilters = filter.sites.filter((f) => f.type === SiteFilterType.RANGE);
      if (rangeFilters.length > 0) {
        const addresses: string[] = [];
        pushOneIfNotUndefined(addresses, history.addr_src);
        pushOneIfNotUndefined(addresses, history.addr_dst);
        if (addresses.length === 0) return false;

        const found = rangeFilters.find((a) => addresses.find((b) => {
          const rangeFilter = a as RangeSiteFilter;
          const ip = ipToNumber(b);
          const first = ipToNumber(rangeFilter.first);
          const final = ipToNumber(rangeFilter.final);
          return first <= ip && ip <= final;
        }));
        if (!found) return false;
      }

      // Filter sites by network
      const networkFilters = filter.sites.filter((f) => f.type === SiteFilterType.NETWORK);
      if (networkFilters.length > 0) {
        const addresses: string[] = [];
        pushOneIfNotUndefined(addresses, history.addr_src);
        pushOneIfNotUndefined(addresses, history.addr_dst);
        if (addresses.length === 0) return false;

        const found = networkFilters.find((a) => addresses.find((b) => {
          const networkFilter = a as NetworkSiteFilter;
          const ip = ipToNumber(b);
          const net = ipToNumber(networkFilter.address);
          const first = netFirst(net, networkFilter.mask);
          const final = netFinal(net, networkFilter.mask);
          return first <= ip && ip <= final;
        }));
        if (!found) return false;
      }
    }

    // Filter devices
    if (filter.devices.length > 0) {
      const devices: string[] = [];
      pushOneIfNotUndefined(devices, domain.hosts.getMac(history.mac_src)?.id);
      pushOneIfNotUndefined(devices, domain.hosts.getMac(history.mac_dst)?.id);
      if (devices.length === 0) return false;

      const found = filter.devices.find((a) => devices.find((b) => a === b));
      if (!found) return false;
    }

    // Filter locations
    if (filter.locations.length > 0) {
      const locations: CountryCode[] = [];
      pushOneIfNotUndefined(locations, history.location_src?.country_short);
      pushOneIfNotUndefined(locations, history.location_dst?.country_short);
      if (locations.length === 0) return false;

      const found = filter.locations.some((a) => locations.indexOf(a) !== -1);
      if (!found) return false;
    }

    return true;
  };
}

function parseHistoryResponse(httpResp: AxiosResponse): HistoryMap {
  const rawBytes = getResponseBytes(httpResp);
  const rootProto = GetNetworkHistoryResponse.deserializeBinary(rawBytes);
  const listProto = rootProto.getHistory().getItemsList();
  const historyObjects = listProto.map((p) => historyFromProto(p));
  const historyPairs = historyObjects.filter(notUndefined).map((h) => [serializeHistoryIndex(h), h]);
  return new HistoryMap(historyPairs.filter((h): h is [string, History] => notUndefined(h[0])));
}

export function historyFromProto(proto: NetworkHistoryObject): History {
  return {
    order: proto.getOrder() ?? 0,
    ctid: proto.getCtid() ?? 0,
    start: proto.getStart() ?? 0,
    last: proto.getLast() ?? 0,
    direction: proto.getDirection() ?? NetworkHistoryDirection.N_H_DIRECTION_INVALID,
    mac_src: proto.getMacSrc() ?? '',
    mac_dst: proto.getMacDst() ?? '',
    proto_l3: proto.getProtoL3() ?? 0,
    addr_src: proto.getAddrSrc() ?? '',
    addr_dst: proto.getAddrDst() ?? '',
    proto_l4: proto.getProtoL4() ?? 0,
    port_src: proto.getPortSrc() ?? 0,
    port_dst: proto.getPortDst() ?? 0,
    pkts_orig: proto.getPktsOrig() ?? 0,
    pkts_repl: proto.getPktsRepl() ?? 0,
    bytes_orig: proto.getBytesOrig() ?? 0,
    bytes_repl: proto.getBytesRepl() ?? 0,
    allowed: proto.getAllowed() ?? false,
    domains_src: proto.getDomainsSrcList(),
    domains_dst: proto.getDomainsDstList(),
    location_src: createHistoryLocation(proto.getLocationSrc()),
    location_dst: createHistoryLocation(proto.getLocationDst()),
  };
}

export function createHistoryLocation(proto: NetworkHistoryLocationObject | undefined): HistoryLocation | undefined {
  return proto ? {
    country_short: proto.getCountryShort() ?? '',
    country_long: proto.getCountryLong() ?? '',
    region: proto.getRegion() ?? '',
    city: proto.getCity() ?? '',
    zipcode: proto.getZipcode() ?? '',
    timezone: proto.getTimezone() ?? '',
    ip: proto.getIp() ?? '',
    longitude: proto.getLongitude() ?? 0,
    latitude: proto.getLatitude() ?? 0,
  } : undefined;
}

export function serializeHistoryIndex(index: HistoryIndex): string | undefined {
  try {
    // verify field values
    if (isNaN(index.order) || index.order < 0) return;
    if (isNaN(index.ctid) || index.ctid < 0) return;
    if (isNaN(index.start) || index.start < 0) return;

    // uint64; max of 8 bytes; max of 16 characters
    const orderHex = index.order.toString(16);
    // uint32; max of 4 bytes; max of 8 characters
    const ctidHex = index.ctid.toString(16);
    // uint64; max of 8 bytes; max of 16 characters
    const startHex = index.start.toString(16);

    // why subtract lengths by 1 when encoding?
    // 1. so that max length of 16 can be presented by 0xf
    //    meaning max 1 character to represent length
    // 2. length will never be zero, so result will never be
    //    a negative number
    const orderLength = (orderHex.length - 1).toString(16);
    const ctidLength = (ctidHex.length - 1).toString(16);
    const startLength = (startHex.length - 1).toString(16);

    // concatenate fields into a string
    return `${orderLength}${ctidLength}${startLength}${orderHex}${ctidHex}${startHex}`;
  } catch (error) {
    return;
  }
}

export function deserializeHistoryIndex(index: string): HistoryIndex | undefined {
  try {
    // parse field lengths
    const orderLength = parseInt(index.substring(0, 1), 16) + 1;
    const ctidLength = parseInt(index.substring(1, 2), 16) + 1;
    const startLength = parseInt(index.substring(2, 3), 16) + 1;

    // compute field positions
    const orderIndex = 3;
    const ctidIndex = orderIndex + orderLength;
    const startIndex = ctidIndex + ctidLength;

    // extract fields
    const order = parseInt(index.substring(orderIndex, orderIndex + orderLength), 16);
    const ctid = parseInt(index.substring(ctidIndex, ctidIndex + ctidLength), 16);
    const start = parseInt(index.substring(startIndex, startIndex + startLength), 16);

    // return as object
    return { order, ctid, start };
  } catch (error) {
    return undefined;
  }
}
